import React from 'react';
import { motion } from 'framer-motion';
import Hero from '../../../renderer/components/Hero';
import Component from '../../../renderer/components/Component';
import ButtonCollection from '../../../renderer/components/ButtonCollection';
import { PlusIcon, InfoIcon, LayoutIcon } from '../../../design-system/icons';
import { typography, surfaces, borders, textColors } from '../../../design-system/tokens';
import { EditContext } from '../../context/EditContext';

const TEMPLATE_PREVIEW_LOG_TITLE = 'TEMPLATE_PREVIEW_DEBUG';

const PREVIEW_CONFIG = {
  // Use a small horizontal overscan so previews feel edge-to-edge (removes side gutters)
  component: { height: 160, scale: 0.47, overscanX: 1.12 },
  // Hero previews should render in "desktop" breakpoints so multi-column layouts are visible,
  // even though the card itself is narrow.
  hero: { height: 165, scale: 0.46, minContainerWidth: 1024, overscanX: 1.18 },
  button_collection: { height: 120, scale: 0.56, overscanX: 1.12 },
};

const TemplatePreview = ({ elementType, template }) => {
  const containerRef = React.useRef(null);
  const [measuredWidth, setMeasuredWidth] = React.useState(960);
  const EmptyIcon = elementType === 'component' ? LayoutIcon : InfoIcon;
  // Larger top padding for better visual balance in empty preview state.
  // Keep it smaller for shorter preview cards (e.g., button collections) to avoid clipping.
  const emptyPaddingClass =
    elementType === 'button_collection' ? 'pt-8 pb-5' : 'pt-12 pb-8';

  React.useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const nextWidth = entry.contentRect.width;
      setMeasuredWidth((prev) => {
        if (!Number.isFinite(nextWidth)) return prev;
        // Avoid continuous state updates from sub-pixel changes
        return Math.abs(prev - nextWidth) > 1 ? nextWidth : prev;
      });
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const EmptyPreviewState = ({ message }) => (
    <div
      ref={containerRef}
      className={`h-full w-full flex flex-col items-center justify-center text-center px-4 ${emptyPaddingClass} ${textColors.tertiary}`}
    >
      {/* Use a component-specific icon for component previews */}
      <EmptyIcon size="2xl" color="inherit" className="mb-2" />
      <div className={`${typography.bodySmall} ${textColors.secondary}`}>{message}</div>
    </div>
  );

  if (!template.preview_data) {
    console.warn(`${TEMPLATE_PREVIEW_LOG_TITLE} renderPreview missing preview_data`, {
      elementType,
      templateId: template?.id,
      templateKeys: template ? Object.keys(template) : null,
    });
    return <EmptyPreviewState message="No preview available" />;
  }

  if (!PREVIEW_CONFIG[elementType]) {
    return <EmptyPreviewState message="Unsupported preview" />;
  }

  const { height, scale, minContainerWidth, overscanX } = PREVIEW_CONFIG[elementType];
  const effectiveWidth = Math.max(measuredWidth, minContainerWidth ?? 360);
  const safeOverscanX = Number.isFinite(overscanX) && overscanX > 0 ? overscanX : 1;

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-hidden ${surfaces.secondary} flex items-center justify-center w-full`}
      style={{ height }}
    >
      <div 
        className="pointer-events-none [&>*]:!p-0 [&>*]:!m-0"
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
          // Keep previews edge-to-edge while slightly cropping the sides
          // (overscan helps remove visible gutters from many background patterns)
          width: `${(100 * safeOverscanX) / scale}%`,
        }}
      >
        <EditContext.Provider value={null}>
          {elementType === 'component' && (
            <Component item={template.preview_data} containerWidth={effectiveWidth} />
          )}
          {elementType === 'hero' && (
            <Hero item={template.preview_data} containerWidth={effectiveWidth} />
          )}
          {elementType === 'button_collection' && (
            <ButtonCollection item={template.preview_data} containerWidth={effectiveWidth} />
          )}
        </EditContext.Provider>
      </div>
    </div>
  );
};

const TemplatesList = ({ elementType, templates, onSelectTemplate, isLoading }) => {
  // Empty state - no element type selected
  if (!elementType) {
    return (
      <div className={`flex flex-col items-center justify-center h-full ${textColors.tertiary} p-8`}>
        <svg className={`w-16 h-16 mb-4 ${textColors.muted}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        <p className={`${typography.body} text-center`}>Select an element type to see templates</p>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className={`${typography.body} ${textColors.secondary}`}>Loading templates...</div>
      </div>
    );
  }

  // No templates available for this type
  if (!templates || templates.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center h-full ${textColors.tertiary} p-8`}>
        <p className={`${typography.body} text-center`}>No templates available for {elementType}</p>
      </div>
    );
  }

  // Get section title based on element type
  const getSectionTitle = () => {
    switch (elementType) {
      case 'hero':
        return 'Hero Previews';
      case 'component':
        return 'Component Previews';
      case 'button_collection':
        return 'Button Collection Previews';
      default:
        return null;
    }
  };

  const sectionTitle = getSectionTitle();

  // Render template cards
  return (
    <div className="pb-8">
      {sectionTitle && (
        <h3 className={`${typography.h5} ${textColors.primary} font-semibold mb-4 px-4`}>
          {sectionTitle}
        </h3>
      )}
      <div className="grid grid-cols-1 gap-3">
        {templates.map((template, index) => (
        <motion.button
          key={template.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05, duration: 0.2 }}
          onClick={() => onSelectTemplate(template)}
          className={`w-full block p-3 border-2 ${borders.default} rounded-lg hover:border-blue-600 dark:hover:border-blue-400 hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 text-left cursor-pointer group ${surfaces.tertiary}`}
        >
          {/* Preview */}
          <div className="mb-2 -mx-3 -mt-3 overflow-hidden">
            <TemplatePreview elementType={elementType} template={template} />
          </div>
          
          {/* Name only */}
          <div className="flex items-center justify-between">
            <div className={`${typography.body} ${textColors.primary} font-medium group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors`}>
              {template.name}
            </div>
            <PlusIcon 
              size="md" 
              className={`${textColors.tertiary} group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors flex-shrink-0`}
            />
          </div>
        </motion.button>
        ))}
      </div>
    </div>
  );
};

export default TemplatesList;
